/**
 * The admin console's manual-entitlement save (#2029).
 *
 * The console used to write the user's WHOLE manual set back through
 * `PUT /v1/admin/users/{uid}/manual-entitlements`, so a console left open
 * silently undid every change made elsewhere since it loaded. It now sends only
 * the grants the admin toggled, one at a time, through #2028's per-grant
 * routes. This module is the pure part of that: which ids are held, what the
 * admin changed, and the sequential runner with its failure classification and
 * report. It has no React in it, so every rule here is unit-tested directly.
 *
 * The guarantee: an id the admin did not toggle is never written, whatever
 * happened to it meanwhile. An id the admin did toggle applies that intent
 * against current storage — last intent wins on that one id, including on a
 * Retry made minutes later, which is why the report says when the batch began.
 */

import { ApiHttpError } from './authenticated-api';
import type {
  ApiResponse,
  ManualEntitlementChangeResponse,
} from './authenticated-api';
import type { UserEntitlements } from './types/entitlement';

/** One per-grant call: an add (POST) or a remove (DELETE). */
export interface ManualEntitlementOp {
  kind: 'add' | 'remove';
  entitlementId: string;
}

/** The two per-grant calls, as `authenticatedApi` provides them. */
export interface ManualEntitlementApi {
  addManualEntitlement(
    uid: string,
    entitlementId: string,
    reason: string
  ): Promise<ApiResponse<ManualEntitlementChangeResponse>>;
  removeManualEntitlement(
    uid: string,
    entitlementId: string,
    reason: string
  ): Promise<ApiResponse<ManualEntitlementChangeResponse>>;
}

/** A failed call, with the HTTP status when there was a response at all. */
export interface ManualEntitlementOpFailure {
  op: ManualEntitlementOp;
  /** `null` for a network error or a client timeout: no response arrived. */
  status: number | null;
  message: string;
}

/**
 * Everything a batch has done so far, across its Save and any Retries.
 *
 * `pending` is what Retry replays, verbatim and in order. It is
 * `[the failed op if retryable, ...notAttempted]`.
 */
export interface ManualEntitlementBatch {
  /** When Save was clicked. A Retry keeps it. */
  startedAt: Date;
  /** Ops that returned 200, in the order they did. */
  applied: ManualEntitlementOp[];
  /** Ops answered with a non-retryable 4xx. They leave `pending`. */
  refused: ManualEntitlementOpFailure[];
  /**
   * Ops a 5xx, network error or timeout left unconfirmed: the per-grant routes
   * commit before they re-sync the claims, so such an op may be stored without
   * its access update. The mark is STICKY — only a later success at the same
   * op clears it; a later refusal does not.
   */
  maySaved: ManualEntitlementOpFailure[];
  /**
   * The last attempt's retryable failure (a 5xx, network error, timeout, 408
   * or 429), which heads `pending`. `null` when the last attempt ended any
   * other way.
   */
  retryableFailure: ManualEntitlementOpFailure | null;
  /** Ops the last attempt never reached. */
  notAttempted: ManualEntitlementOp[];
  /** What Retry would send. Empty once the batch is over. */
  pending: ManualEntitlementOp[];
  /** Whether the last attempt ended in a refusal ("Continue with remaining"). */
  lastAttemptRefused: boolean;
}

/**
 * The manual ids an admin currently holds, from the merged
 * `GET /v1/users/{uid}/entitlements` payload.
 *
 * `'manual'` is in `sources` only while the manual grant is active AND
 * unexpired, so an expired or inactive manual record is not "held" here: it
 * shows unticked, is never sent unless the admin ticks it, and stays stored.
 * Subscription-only access is not manual, and is never in this list.
 */
export function deriveManualEntitlementIds(
  entitlements: UserEntitlements
): string[] {
  return Object.entries(entitlements)
    .filter(
      ([, detail]) => detail.isActive && detail.sources?.includes('manual')
    )
    .map(([id]) => id);
}

/**
 * The admin's toggles, as ops: adds (sorted), then removes (sorted).
 *
 * Both sides come from ONE frozen snapshot, so the diff holds only what the
 * admin changed, however stale the snapshot has become. Adds go first so that
 * swapping one role for another never leaves a gap with neither.
 */
export function diffManualEntitlements(
  baseline: readonly string[],
  selection: readonly string[]
): ManualEntitlementOp[] {
  const before = new Set(baseline);
  const after = new Set(selection);
  const adds = [...after].filter(id => !before.has(id)).sort();
  const removes = [...before].filter(id => !after.has(id)).sort();
  return [
    ...adds.map(entitlementId => ({ kind: 'add' as const, entitlementId })),
    ...removes.map(entitlementId => ({
      kind: 'remove' as const,
      entitlementId,
    })),
  ];
}

/** `+hlive:host` for an add, `−hlive:host` for a remove. */
export function formatOp(op: ManualEntitlementOp): string {
  return `${op.kind === 'add' ? '+' : '−'}${op.entitlementId}`;
}

const sameOp = (a: ManualEntitlementOp, b: ManualEntitlementOp): boolean =>
  a.kind === b.kind && a.entitlementId === b.entitlementId;

/**
 * How a failed call is treated.
 *
 * - a 5xx, a network error or a client timeout: retryable, AND it may have
 *   committed (the claims sync runs after the commit), so it is marked
 *   "may be saved";
 * - `408` or `429`: retryable, and the request was not processed;
 * - any other 4xx: refused. Re-sending it would be answered the same way.
 */
export function classifyManualEntitlementFailure(error: unknown): {
  status: number | null;
  retryable: boolean;
  maySave: boolean;
} {
  if (error instanceof ApiHttpError) {
    const { status } = error;
    if (status >= 500) return { status, retryable: true, maySave: true };
    if (status === 408 || status === 429) {
      return { status, retryable: true, maySave: false };
    }
    return { status, retryable: false, maySave: false };
  }
  // No response: fetch rejected (network) or our own 30s abort fired.
  return { status: null, retryable: true, maySave: true };
}

/** `HTTP 400: Cannot remove…` → `Cannot remove…`; the status is shown apart. */
function failureMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.replace(/^HTTP \d+: /, '');
}

function describeFailure(failure: ManualEntitlementOpFailure): string {
  const status = failure.status === null ? 'no response' : failure.status;
  return `${status}: ${failure.message}`;
}

/** `2026-09-24 12:00:00 UTC`. */
function formatStartedAt(date: Date): string {
  return `${date.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

/**
 * The report: what was saved, refused, may be saved, and not attempted, each
 * section omitted when empty.
 */
export function formatManualEntitlementReport(
  batch: ManualEntitlementBatch
): string {
  const parts: string[] = [
    `Batch started ${formatStartedAt(batch.startedAt)}.`,
  ];
  const isPending = (op: ManualEntitlementOp) =>
    batch.pending.some(p => sameOp(p, op));
  const isMaySaved = (op: ManualEntitlementOp) =>
    batch.maySaved.some(m => sameOp(m.op, op));

  if (batch.applied.length > 0) {
    parts.push(`Saved: ${batch.applied.map(formatOp).join(', ')}.`);
  }

  // A refused op that still carries "may be saved" is reported there instead.
  const refused = batch.refused.filter(f => !isMaySaved(f.op));
  if (refused.length > 0) {
    parts.push(
      `Refused: ${refused
        .map(f => `${formatOp(f.op)} (${describeFailure(f)})`)
        .join(', ')}.`
    );
  }

  if (batch.maySaved.length > 0) {
    const items = batch.maySaved.map(f => {
      const refusal = batch.refused.find(r => sameOp(r.op, f.op));
      if (refusal) {
        return `${formatOp(f.op)} (${describeFailure(f)}; then refused, ${describeFailure(refusal)}; access update not confirmed)`;
      }
      // A retry that failed differently (a 500, then a 429) keeps the mark,
      // but the admin needs to see what the latest attempt got.
      const last = batch.retryableFailure;
      const lastAttempt =
        last &&
        sameOp(last.op, f.op) &&
        (last.status !== f.status || last.message !== f.message)
          ? `; last attempt ${describeFailure(last)}`
          : '';
      return `${formatOp(f.op)} (${describeFailure(f)}${lastAttempt})`;
    });
    const retrying = batch.maySaved.some(f => isPending(f.op))
      ? '; retrying completes it'
      : '';
    parts.push(`May be saved, not confirmed: ${items.join(', ')}${retrying}.`);
  }

  const failure = batch.retryableFailure;
  if (failure && !isMaySaved(failure.op)) {
    parts.push(
      `Not saved: ${formatOp(failure.op)} (${describeFailure(failure)}); retrying sends it again.`
    );
  }

  if (batch.notAttempted.length > 0) {
    parts.push(
      `Not attempted: ${batch.notAttempted.map(formatOp).join(', ')}.`
    );
  }

  return parts.join(' ');
}

/**
 * Thrown by `applyManualEntitlementChanges` when a call fails. Its `message`
 * is the formatted report, so a plain `err.message` display shows it.
 */
export class ManualEntitlementSaveError extends Error {
  readonly batch: ManualEntitlementBatch;

  constructor(batch: ManualEntitlementBatch) {
    super(formatManualEntitlementReport(batch));
    this.name = 'ManualEntitlementSaveError';
    this.batch = batch;
  }

  get applied(): ManualEntitlementOp[] {
    return this.batch.applied;
  }

  get refused(): ManualEntitlementOpFailure[] {
    return this.batch.refused;
  }

  get maySaved(): ManualEntitlementOpFailure[] {
    return this.batch.maySaved;
  }

  get notAttempted(): ManualEntitlementOp[] {
    return this.batch.notAttempted;
  }

  get pending(): ManualEntitlementOp[] {
    return this.batch.pending;
  }

  get startedAt(): Date {
    return this.batch.startedAt;
  }
}

/**
 * Whether a batch that has no `pending` left ended cleanly: nothing refused
 * and nothing left "may be saved". Only then does the editor close by itself.
 */
export function isCleanManualEntitlementBatch(
  batch: ManualEntitlementBatch
): boolean {
  return (
    batch.pending.length === 0 &&
    batch.refused.length === 0 &&
    batch.maySaved.length === 0
  );
}

/**
 * Run `ops` one at a time, in order, with the one reason, and stop at the
 * first failure.
 *
 * Resolves with the batch when every op returned 200 (which may still carry
 * refusals or "may be saved" from an earlier attempt: see
 * `isCleanManualEntitlementBatch`). Rejects with `ManualEntitlementSaveError`
 * at the first failure; its `pending` is what a Retry passes back as `ops`,
 * with the batch as `options.previous`, so the sticky marks and the start
 * time carry over.
 *
 * `options.signal`: once aborted (the editor unmounted), no further call is
 * issued; the one in flight is left to finish.
 */
export async function applyManualEntitlementChanges(
  api: ManualEntitlementApi,
  uid: string,
  ops: readonly ManualEntitlementOp[],
  reason: string,
  onProgress?: (current: number, total: number) => void,
  options: { previous?: ManualEntitlementBatch; signal?: AbortSignal } = {}
): Promise<ManualEntitlementBatch> {
  const { previous, signal } = options;
  const applied = [...(previous?.applied ?? [])];
  const refused = [...(previous?.refused ?? [])];
  let maySaved = [...(previous?.maySaved ?? [])];
  const startedAt = previous?.startedAt ?? new Date();

  const stop = (
    index: number,
    failure: ManualEntitlementOpFailure | null,
    retryable: boolean
  ): ManualEntitlementSaveError => {
    const notAttempted = ops.slice(failure ? index + 1 : index);
    return new ManualEntitlementSaveError({
      startedAt,
      applied,
      refused,
      maySaved,
      retryableFailure: failure && retryable ? failure : null,
      notAttempted,
      pending:
        failure && retryable ? [failure.op, ...notAttempted] : notAttempted,
      lastAttemptRefused: failure !== null && !retryable,
    });
  };

  for (let index = 0; index < ops.length; index++) {
    if (signal?.aborted) throw stop(index, null, false);

    const op = ops[index];
    onProgress?.(index + 1, ops.length);
    try {
      if (op.kind === 'add') {
        await api.addManualEntitlement(uid, op.entitlementId, reason);
      } else {
        await api.removeManualEntitlement(uid, op.entitlementId, reason);
      }
      applied.push(op);
      maySaved = maySaved.filter(f => !sameOp(f.op, op));
    } catch (error) {
      const { status, retryable, maySave } =
        classifyManualEntitlementFailure(error);
      const failure: ManualEntitlementOpFailure = {
        op,
        status,
        message: failureMessage(error),
      };
      if (maySave) {
        maySaved = [...maySaved.filter(f => !sameOp(f.op, op)), failure];
      }
      if (!retryable) refused.push(failure);
      throw stop(index, failure, retryable);
    }
  }

  return {
    startedAt,
    applied,
    refused,
    maySaved,
    retryableFailure: null,
    notAttempted: [],
    pending: [],
    lastAttemptRefused: false,
  };
}
